import { ChangeCircle, Close } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { authContext, Company } from '@staffedupusa/shared-components';
import React, { FC, useContext, useEffect, useState } from 'react';
import { lightGrey2, white } from '@styles/colors';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import ConfirmationUpdateBillingModal from './ConfirmationUpdateBillingModal';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import SelectPlanForExpired from './SelectPlanForExpired';

type ExtendedApplication = Company & {
  is_company_paid: boolean;
  sub_users?: { length: number }[];
  subscription?: {
    date_created: string;
    date_updated: string;
    id: number;
    stripe_id: string;
    billing_cycle?: string;
    next_billing_date?: string;
    total_amount?: number;
  };
};

type CompanyPaymentDetailModalProps = {
  getPlanDetail: (plan: number) => string;
  company: ExtendedApplication;
  handleClose: () => void;
  openModal: boolean;
  totalAggregate: any;
  getBackgroundColor: (company: Company) => string;
  getLabel: (company: Company) => string;
};

const CompanyPaymentDetailModal: FC<CompanyPaymentDetailModalProps> = ({
  company,
  totalAggregate,
  getPlanDetail,
  handleClose,
  openModal,
  getBackgroundColor,
  getLabel,
}) => {
  const {
    s_name,
    plan,
    is_company_paid,
  } = company;
  const { authData } = useContext(authContext)
  const { paymentInfo } = useGetPaymantDetails();
  const [showConfirmationModal, setShowConfirmationModal] = useState('')
  const [showSelectPlanModal, setShowSelectPlanModal] = useState(false)
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const navigate = useNavigate()

  useEffect(() => {
    if (
      ((paymentInfo &&
        paymentInfo?.object &&
        // @ts-ignore
        paymentInfo?.object?.data?.length > 0) && (authData?.user?.billing_cycle > 0))
    ) {
      setHasPaymentMethod(true);
    }
  }, [paymentInfo]);

  const renderPlanAction = () => {
    if (!is_company_paid) {
      return (
        <Button
          startIcon={<KeyboardDoubleArrowUpIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            if (hasPaymentMethod) {
              setShowSelectPlanModal(true)
            } else {
              toast.error("Please Attach a Payment Method and  billing Cycle to upgrade ");
              navigate('#Payment-Info');
            }
          }}
        >
          Change plan
        </Button>
      );
    }
  };

  const renderPaymentAction = () => {
    if (!is_company_paid) {
      return (
        <Button
          onClick={() => setShowConfirmationModal('self paid')}
          variant="outlined"
          color="warning"
          startIcon={<DoubleArrowIcon />}
        >
          Move to location billing
        </Button>
      );
    } else {
      return (
        <Button
          onClick={() => {
            if (hasPaymentMethod) {
              setShowConfirmationModal('enterprise paid');
            } else {
              toast.error(
                'Please Attach a Payment Method To Move to Enterprise billing',
              );
              navigate('#Payment-Info');
            }
          }}
          variant="outlined"
          color="success"
          startIcon={<DoubleArrowIcon color="success" />}
        >
          Move to enterprise billing
        </Button>
      );
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth="xs" open={openModal} onClose={handleClose}>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={2}>
            <Stack direction="row" alignItems="center">
              <Avatar
                src={(company.s_logo_file as string) || ''}
                alt={s_name || ''}
                style={{
                  width: '100px',
                  height: '100px',
                  marginRight: '12px',
                  borderRadius: 5,
                }}
              />
              <Stack spacing={1}>
                <Typography variant="h4">{s_name}</Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="textSecondary">
                    Plan: {getPlanDetail(plan)}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    backgroundColor: getBackgroundColor(company),
                    color: white,
                    padding: '1px 15px',
                    textAlign: 'center',
                    borderRadius: 5,
                    width: 'fit-content',
                  }}
                >
                  {getLabel(company)}
                </Box>
              </Stack>
            </Stack>
            {company?.sub_users && company?.sub_users?.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              Total subusers: {company.sub_users?.length}
            </Typography>
            )}

            {company?.is_company_paid && company.plan !== 0 && (
              <Typography
                variant="subtitle2"
                color={lightGrey2}
                gutterBottom
              >
                <span style={{textTransform:'capitalize'}}>
                {company.subscription?.billing_cycle
                ? `${company.subscription.billing_cycle}ly`
                : ''}
                </span>
                {company.subscription?.next_billing_date ? (
                  <>
                    {' '}
                    (Next invoice: <>{company.subscription.next_billing_date}</>
                    )
                  </>
                ) : (
                  ''
                )}
              </Typography>
            )}
            <Stack direction="column" spacing={2}>
              {renderPlanAction()}
              {renderPaymentAction()}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      {showConfirmationModal ? (
        <ConfirmationUpdateBillingModal
          isOpen={showConfirmationModal}
          handleClose={() => setShowConfirmationModal('')}
          companyName={s_name}
          companyId={company?.id}
          isExpired={(((plan === 0) || (plan === 999))) ? true : false}
          hasPaymentMethod={hasPaymentMethod}
          plan={plan}
        />
      ) : null}
      {showSelectPlanModal ? (
        <SelectPlanForExpired
          open={showSelectPlanModal}
          handleClose={() => setShowSelectPlanModal(false)}
          companyName={s_name}
          companyId={company?.id}
          currentPlan={plan as 1 | 2 | 4 | 0 | 999}
          hasPaymentMethod={hasPaymentMethod}
        />
      ) : null}
    </>
  );
};

export default CompanyPaymentDetailModal;